import { Injectable, inject } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, Observable } from 'rxjs';

import { ProductLinksDictionary } from 'ish-core/models/product-links/product-links.model';

export interface CrossSellingPopupEvent {
  sku: string; // single product event
  productLinks: ProductLinksDictionary;
  timestamp: number;
  quantity: number;
}

export interface CrossSellingPopupMergedEvent {
  skus: string[]; // aggregated
  productLinks: ProductLinksDictionary;
  timestamp: number;
  quantity: number[];
}

/**
 * Global service to manage cross-selling popup events
 * Singleton service that all ProductContextFacade instances can emit to
 */
@Injectable({ providedIn: 'root' })
export class CrossSellingPopupService {
  private modalService = inject(NgbModal);

  // eslint-disable-next-line unicorn/no-null
  private crossSellingPopupSubject$ = new BehaviorSubject<CrossSellingPopupMergedEvent | null>(null);

  /**
   * Observable stream that emits when a product with cross-selling items is added to basket
   */
  // eslint-disable-next-line unicorn/no-null
  crossSellingPopup$: Observable<CrossSellingPopupMergedEvent | null> = this.crossSellingPopupSubject$.asObservable();

  // store aggregated products
  private masterProducts = new Map<string, CrossSellingPopupEvent>();

  emitCrossSellingEvent(event: CrossSellingPopupEvent) {
    if (this.modalService.hasOpenModals()) {
      this.modalService.dismissAll('Cross-selling popup opened');
    }

    // store unique master product
    this.masterProducts.set(event.sku, event);

    const mergedEvent = this.mergeEvents();

    this.crossSellingPopupSubject$.next(mergedEvent);
  }

  private mergeEvents(): CrossSellingPopupMergedEvent {
    const events = Array.from(this.masterProducts.values());

    const mergedProducts = Array.from(new Set(events.flatMap(e => e.productLinks?.crossselling?.products || [])));

    const mergedCategories = Array.from(new Set(events.flatMap(e => e.productLinks?.crossselling?.categories || [])));

    const mergedLinks: ProductLinksDictionary = {
      crossselling: {
        products: mergedProducts,
        categories: mergedCategories,
      },
    };

    return {
      skus: events.map(e => e.sku),
      productLinks: mergedLinks,
      quantity: events.map(e => e.quantity),
      timestamp: Date.now(),
    };
  }

  dismissPopup() {
    // eslint-disable-next-line unicorn/no-null
    this.masterProducts.clear();
    this.crossSellingPopupSubject$.next(null);
  }
}
